<template>
  <div
    v-if="newsItemId && points.length"
    :class="COMPONENT_CLASS_NAME"
    :qa-locator-id="locators.set()"
  >
    <div class="stepper-title" :qa-locator-id="locators.set('name')">
      {{ name }}
    </div>
    <div :id="`stepper-content-${key}`" class="stepper-content">
      <div
        v-for="(item, index) of points"
        :key="index"
        class="step"
        :class="{ active: selectedIndex === index }"
        :qa-locator-id="locators.set('item')"
        @click="scrollToTitle(index)"
      >
        {{ item.name }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick, computed, watch } from 'vue';
import { useQaLocators } from '~/composables/use-qa-locators';

/*
 * Базовый класс компонента
 */
const COMPONENT_CLASS_NAME = 'stepper';

const { $emitter } = useNuxtApp();
const locators = useQaLocators(COMPONENT_CLASS_NAME);
const route = useRoute();

/*
 * Интерфейс для элемента содержания статьи
 * @interface PointInterface
 * @property {string} name - Название пункта содержания
 * @property {number} top - Позиция элемента по вертикали на странице
 */
interface PointInterface {
  name: string;
  top: number;
}

/*
 * Определение событий, которые может генерировать компонент
 */
const emit = defineEmits<{
  /*
   * Событие при клике на пункт содержания
   */
  'on-click-step': [];
  /*
   * Событие при изменении значения прогресса
   * @param value - Флаг наличия содержания
   */
  'on-change-progress-value': [value: boolean];
}>();

/*
 * Определение входных параметров компонента
 */
const props = defineProps({
  /*
   * Идентификатор новости
   */
  newsItemId: {
    type: Number,
    required: true,
  },
  /*
   * Заголовок блока содержания
   */
  name: {
    type: String,
    default: 'Содержание статьи',
  },
});

/*
 * Уникальный ключ для идентификации элементов содержания
 */
const key = computed(() => props.newsItemId ?? Math.floor(Math.random() * 10000));

/*
 * Ссылки на HTML-элементы заголовков
 */
const pointsElements = ref<HTMLHeadingElement[]>([]);

/*
 * Массив пунктов содержания с их позициями
 */
const points = ref<PointInterface[]>([]);

/*
 * Индекс выбранного пункта содержания
 */
const selectedIndex = ref(0);

const isTomatoesPage = computed(() => route.path.includes('/tomatoes') && !!route.params.slug);

/*
 * Инициализация компонента при монтировании
 */
onMounted(async () => {
  if (import.meta.client) {
    await fillOffsets();

    $emitter.on('on-helper-mounted', fillOffsets);
    window.addEventListener('scroll', onScroll);

    // Определяем активный элемент при монтировании
    updateActiveElement();
  }
});

/*
 * Очистка обработчиков событий перед размонтированием компонента
 */
onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('scroll', onScroll);
    $emitter.off('on-helper-mounted', fillOffsets);
  }
});

/*
 * Отслеживание изменения ID новости для обновления содержания
 */
watch(
  () => props.newsItemId,
  async () => {
    await nextTick();
    setTimeout(async () => {
      await fillOffsets();
      updateActiveElement();
    }, 250);
  }
);

/*
 * Обновляет активный элемент на основе текущей позиции прокрутки
 */
function updateActiveElement() {
  if (import.meta.client && window) {
    getSelectedIndex(window.scrollY);
    scrollToActiveElement();
  }
}

/*
 * Прокручивает контейнер содержания к активному элементу
 */
function scrollToActiveElement() {
  if (!import.meta.client) return;

  const container = document.querySelector(`#stepper-content-${key.value}`);
  const active = document.querySelector(`#stepper-content-${key.value} > .active`) as HTMLElement;

  if (container && active) {
    container.scrollTo({
      top: active.offsetTop,
      behavior: 'smooth',
    });
  }
}

/*
 * Определяет текущий активный пункт содержания на основе позиции прокрутки
 * @param positionTop - Текущая позиция прокрутки страницы
 */
function getSelectedIndex(positionTop: number): void {
  if (!positionTop || !points.value.length) return;

  // Находим ближайший заголовок к текущей позиции прокрутки
  let closestIndex = 0;
  let minDistance = Infinity;

  points.value.forEach((item, index) => {
    // Учитываем смещение для лучшего определения активного элемента
    const distance = Math.abs(positionTop + 10 - item.top);

    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  // Если мы прокрутили ниже последнего заголовка, выбираем его
  if (positionTop > points.value[points.value.length - 1].top - 100) {
    closestIndex = points.value.length - 1;
  }

  selectedIndex.value = closestIndex;
}

/*
 * Заполняет массив пунктов содержания с их позициями на странице
 * @returns Promise, который разрешается после заполнения массива
 */
async function fillOffsets(): Promise<void> {
  if (!import.meta.client) return;

  points.value = [];

  const container = document.getElementById(`news-item-content-${props.newsItemId}`);

  if (!container) {
    $emitter.emit('on-change-progress-value', false);
    return;
  }

  pointsElements.value = Array.from(container.getElementsByTagName('h2'));

  if (!pointsElements.value.length) {
    $emitter.emit('on-change-progress-value', false);
    return;
  }

  points.value = pointsElements.value.map((item) => {
    return {
      name: item.textContent?.trim() || '',
      top: item.offsetTop,
    };
  });

  $emitter.emit('on-change-progress-value', points.value.length > 0);
}

/*
 * Обработчик события прокрутки страницы
 */
function onScroll(): void {
  if (import.meta.client && window) {
    getSelectedIndex(window.scrollY);
  }
}

/*
 * Прокручивает страницу к выбранному заголовку
 * @param index - Индекс выбранного пункта содержания
 */
function scrollToTitle(index: number): void {
  if (!import.meta.client) return;

  selectedIndex.value = index;

  // в томатах добавляется пункт "характеристики", он вне контента, поэтому у него id=0
  const targetElem = document.getElementById(
    `part-${props.newsItemId}-${index + (isTomatoesPage.value ? 0 : 1)}`
  );

  if (targetElem) {
    const offsetTop = targetElem.offsetTop - 90;
    document.body.scrollTo({ top: offsetTop, behavior: 'smooth' });
    window.scrollTo({ top: offsetTop, behavior: 'smooth' });

    // пока скролл в процессе, запрещаем вызов getSelectedIndex, чтобы не было моргания пунктов
    new Promise(() => {
      const checkScroll = () => {
        if (Math.abs(window.scrollY - offsetTop) < 1) {
          window.removeEventListener('scroll', checkScroll);
          window.addEventListener('scroll', onScroll);
        }
      };
      window.removeEventListener('scroll', onScroll);
      window.addEventListener('scroll', checkScroll);
    });
  }

  emit('on-click-step');
}
</script>

<style scoped lang="scss">
.stepper {
  display: inline-block;
  color: #000;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    border-left: 2px solid #c7eac7;
    height: 100%;
    position: absolute;
    left: 4px;

    @include mobile {
      content: unset;
    }
  }

  @include tablet {
    margin: 0 0 0 4px;
  }

  @include mobile {
    padding: 0 16px 24px;
  }

  > .stepper-title {
    font-size: 18px;
    font-weight: 500;
    line-height: 22px;
    margin: 0 0 20px 20px;

    @include mobile {
      font-size: 20px;
      line-height: 28px;
      text-align: center;
      margin: 0 0 24px;
    }
  }

  > .stepper-content {
    position: relative;
    padding-left: 20px;
    height: calc(100% - 40px);
    overflow: auto;

    @include mobile {
      max-height: calc(100vh - 152px);
    }
  }

  > .stepper-content > .step {
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    cursor: pointer;
    margin-bottom: 10px;
    transition: font-weight $transition ease-in-out;
    -webkit-tap-highlight-color: transparent;

    &::before {
      position: absolute;
      top: 2px;
      margin-left: -21px;
      font-size: 35px;
      transition: color $transition ease-in-out;

      @include mobile {
        content: '•';
      }
    }

    &.active::before {
      content: '•';
      color: $color-svoe-grass;

      @include mobile {
        font-size: 28px;
        color: #000;
      }
    }

    @include mobile {
      margin-bottom: 16px;

      &::before {
        color: $color-button-disabled;
        content: '•';
        position: absolute;
        margin-left: -21px;
        font-size: 28px;
      }
    }

    &.active {
      color: $color-svoe-grass;

      @include mobile {
        color: unset;
        font-weight: 600;
      }
    }

    @media (hover: hover) {
      &:hover {
        color: $color-svoe-grass;
      }
    }
  }
}
</style>
